import {
  CUSTOMER_EMAIL_WAIT_LINE,
  STAFF_REJECTED_DETAIL,
  TICKET_CATEGORIES,
  TICKET_CATEGORY_LABEL,
  TICKET_PRIORITIES,
  TICKET_PRIORITY_LABEL,
  TICKET_ROOT_CAUSE_LABEL,
  TICKET_SOURCES,
  TICKET_SOURCE_LABEL,
  TICKET_STATES,
  TICKET_STATE_LABEL,
  addDaysYmd,
  can,
  localDateOf,
  localIso,
  localWallTimeUtc,
  ticketMachine,
  type ListInput,
  type Role,
  type TicketBoardInput,
  type TicketCategory,
  type TicketEmailFailure,
  type TicketEmailStatus,
  type TicketEmailWait,
  type TicketListInput,
  type TicketPriority,
  type TicketRootCause,
  type TicketSource,
  type TicketState,
  type TicketViaLink,
} from '@servwell/shared';
import type { Tone } from '../ui/Badge';
import { dateOnly, timeInTz } from '../../lib/format';

/**
 * The Tickets page's rules (ops insights Phase 5a, ticket 04), pure, so each is a test: what
 * its address may say, what each view asks the server, the overdue and reply-due wording, the
 * moves the status control offers, and what Resolve still needs. No hooks, no React.
 */

/** List first, and so the default: `/tickets` is the List and never says `?tab=list`. */
export const TICKET_VIEWS = ['list', 'board'] as const;
export type TicketView = (typeof TICKET_VIEWS)[number];

/** The owner filter: the two every office asks about by name, or one person. */
export type OwnerFilter = 'mine' | 'nobody' | (string & {});

/** The filters the address carries, one value each, applied by the server. */
export type TicketFilters = {
  status?: TicketState;
  owner?: OwnerFilter;
  priority?: TicketPriority;
  category?: TicketCategory;
  /** Who raised it: the office, a tech's flag, or the customer from a link. */
  source?: TicketSource;
  /** Only the overdue ones. Absent is every ticket, never "only those on time". */
  overdue?: true;
};

/**
 * Every filter, unset: what "Clear filters" writes. A `Record` over the filters' own keys, so
 * a filter added to `TicketFilters` and forgotten here does not compile, and is never left
 * set by a clear that thinks it cleared everything.
 */
export const NO_FILTERS: Record<keyof TicketFilters, undefined> = {
  status: undefined,
  owner: undefined,
  priority: undefined,
  category: undefined,
  source: undefined,
  overdue: undefined,
};

/** `/tickets`' address: the view, the filters, and the ticket whose panel is open. */
export type TicketsSearch = TicketFilters & {
  tab?: 'board';
  ticket?: string;
};

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const oneOf = <T extends string>(valid: readonly T[], raw: unknown): T | undefined =>
  typeof raw === 'string' && (valid as readonly string[]).includes(raw)
    ? (raw as T)
    : undefined;

const uuidIn = (raw: unknown): string | undefined =>
  typeof raw === 'string' && UUID.test(raw) ? raw : undefined;

/**
 * `/tickets`' `validateSearch`, on the `tabSearch` contract (`ui/pageTabs.ts`): a value the
 * page can use is kept, and junk, absence and the defaults are all left out, so a link the app
 * writes has one spelling. `?ticket=` must be an id (a deep link from the bell, MCP or the job
 * page); anything else opens nothing.
 */
export function ticketsSearch(raw: Record<string, unknown>): TicketsSearch {
  const out: TicketsSearch = {};
  if (raw.tab === 'board') out.tab = 'board';
  const status = oneOf(TICKET_STATES, raw.status);
  if (status) out.status = status;
  const owner =
    raw.owner === 'mine' || raw.owner === 'nobody' ? raw.owner : uuidIn(raw.owner);
  if (owner) out.owner = owner;
  const priority = oneOf(TICKET_PRIORITIES, raw.priority);
  if (priority) out.priority = priority;
  const category = oneOf(TICKET_CATEGORIES, raw.category);
  if (category) out.category = category;
  const source = oneOf(TICKET_SOURCES, raw.source);
  if (source) out.source = source;
  // The router parses `?overdue=true` to a boolean; a hand-typed one may still be text.
  if (raw.overdue === true || raw.overdue === 'true') out.overdue = true;
  const ticket = uuidIn(raw.ticket);
  if (ticket) out.ticket = ticket;
  return out;
}

/** The filters in an address, without the view and the open ticket. */
export function filtersOf(search: TicketsSearch): TicketFilters {
  const { tab: _tab, ticket: _ticket, ...filters } = search;
  return filters;
}

/** Any filter set at all: the page offers "Clear filters" only then. */
export function filtering(f: TicketFilters): boolean {
  return Object.values(f).some((v) => v !== undefined);
}

/** The board's half of a filter set: everything but the status, which a column IS. */
function shared(f: TicketFilters) {
  return {
    priority: f.priority ? [f.priority] : undefined,
    category: f.category ? [f.category] : undefined,
    source: f.source ? [f.source] : undefined,
    owner: f.owner,
    overdue: f.overdue,
  };
}

/** What the List asks: the table's own search, sort and page, and the address's filters. */
export function listInputFor(f: TicketFilters, query: ListInput): TicketListInput {
  return {
    ...query,
    ...shared(f),
    status: f.status ? [f.status] : undefined,
  };
}

/** What the Board asks: the same filters and search, no status, no page. */
export function boardInputFor(f: TicketFilters, search?: string): TicketBoardInput {
  return { search, ...shared(f) };
}

/**
 * A Board column's "N more": the List, filtered to that column's status, keeping every other
 * filter the board was showing (the open panel is not carried; the link is to a list).
 */
export function moreInList(f: TicketFilters, status: TicketState): TicketsSearch {
  return { ...f, status };
}

/**
 * Is the linked ticket (`?ticket=`) missing from what the view draws? Only once the view has
 * answered (`shown` null is "not drawn yet", never "missing").
 */
export function linkedOffView(
  openId: string | undefined,
  shown: readonly string[] | null,
): boolean {
  return openId !== undefined && shown !== null && !shown.includes(openId);
}

/** How many cards a column has that it does not draw. */
export function hiddenCount(total: number, shown: number): number {
  return Math.max(0, total - shown);
}

/** How long ago, as the overdue line says it: "25 min", "2 h", "3 d". */
export function agoLabel(ms: number): string {
  const minutes = Math.max(1, Math.floor(ms / 60_000));
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours} h`;
  return `${Math.floor(hours / 24)} d`;
}

/**
 * "Overdue · 2 h": a reply-due that has passed on a ticket that is not resolved (spec §5, the
 * server's `overdue` in `services/ticket.ts`), or null. Read against the clock the screen
 * holds, so the age moves while the page stays open.
 */
export function overdueLabel(
  t: { replyDueAt: Date | string | null; status: TicketState },
  now: number,
): string | null {
  if (t.replyDueAt === null || t.status === 'resolved') return null;
  const late = now - new Date(t.replyDueAt).getTime();
  if (late <= 0) return null;
  return `Overdue · ${agoLabel(late)}`;
}

/**
 * Why a message for the customer was not emailed, one line per reason the ledger can record
 * (`TICKET_EMAIL_FAILURES`, spec §4). A `Record` over the whole list, so a reason added to it
 * without a line here does not compile (and `tickets-view.test.ts` walks the list too).
 *
 * ⚠️ `no-reply-address` is PAST tense: rows written before MAP 249 were skipped for good, and an
 * address added in Settings later does not send them, so "add a reply address" under an old
 * message would be an instruction that does nothing. (Since MAP 249 a new message WAITS for the
 * confirmed address instead, and says so: `CUSTOMER_EMAIL_WAIT_LINE`, one vocabulary with the
 * detail pages'.) `unreachable` and `interrupted` MAY
 * have gone out (the request left and nothing confirmed it), so they never say "not emailed".
 */
export const TICKET_EMAIL_FAILURE_LINE: Record<TicketEmailFailure, string> = {
  'no-recipient': 'Not emailed: no email on file for this customer',
  'not-configured': 'Not emailed: email isn’t set up yet',
  'no-reply-address': 'Not emailed: the shop had no reply address then',
  rejected: 'Not emailed: the address was refused',
  throttled: 'Not emailed: the mail service was busy',
  unreachable: 'May not have been emailed (no answer from the mail service)',
  interrupted: 'May not have been emailed (no answer from the mail service)',
  unconfirmed: 'May not have been emailed (no answer from the mailbox)',
  internal: 'Not emailed: ServWell hit a problem preparing it',
};

/** What became of one message's email, as `ticket.get` answers it (`TicketDelivery`). */
export interface DeliveryState {
  status: TicketEmailStatus;
  /** Why a queued one is not going yet; null (or absent, an older server) when it is due. */
  waitingFor?: TicketEmailWait | null;
  failure: TicketEmailFailure | null;
  /** Masked by the server (`a•••@domain`); null when there was no address on file. */
  to: string | null;
}

/** A line's tone: done, still going, did not go, or a fact nobody can act on. */
export type DeliveryTone = 'done' | 'pending' | 'problem' | 'quiet';

/**
 * The delivery line under a staff message for the customer (spec §4), or null for a message
 * that is owed no email (internal, or the customer's own words). A message for the customer
 * with no ledger row was written before customer emails existed, and says so.
 */
export function deliveryLine(m: {
  visibility: string;
  authorKind: string;
  delivery: DeliveryState | null;
}): { text: string; tone: DeliveryTone } | null {
  if (m.visibility !== 'customer' || m.authorKind !== 'staff') return null;
  const d = m.delivery;
  if (!d)
    // Nothing anyone can do about it, so not in the warning tone.
    return { text: 'Sent before customer emails started', tone: 'quiet' };
  switch (d.status) {
    case 'queued':
      // Waiting (MAP 249: the send gate's waits), in the detail pages' words. A wait this build
      // does not know (a newer server) still says it is on its way.
      return d.waitingFor && Object.hasOwn(CUSTOMER_EMAIL_WAIT_LINE, d.waitingFor)
        ? { text: CUSTOMER_EMAIL_WAIT_LINE[d.waitingFor], tone: 'pending' }
        : { text: 'Sending…', tone: 'pending' };
    case 'sending':
      return { text: 'Sending…', tone: 'pending' };
    case 'sent':
      return {
        text: d.to ? `Emailed to ${d.to}` : 'Emailed',
        tone: 'done',
      };
    case 'skipped':
    case 'failed':
      // ServWell staff stopped it after a look (MAP 249 spec §4b): not the mail service's no,
      // which is `failed / rejected` ("the address was refused").
      if (d.status === 'skipped' && d.failure === 'rejected') {
        return { text: `Not emailed: ${STAFF_REJECTED_DETAIL}`, tone: 'problem' };
      }
      return {
        // The CHECK gives a skipped or failed row a reason; one this build does not know (a
        // newer server) still says it did not go, rather than nothing.
        text:
          d.failure !== null && Object.hasOwn(TICKET_EMAIL_FAILURE_LINE, d.failure)
            ? TICKET_EMAIL_FAILURE_LINE[d.failure]
            : 'Not emailed',
        tone: 'problem',
      };
  }
}

/**
 * How often the panel asks again while a message's email is still "Sending…": the drain runs
 * each minute, so the line changes on its own rather than only on a reload.
 */
export const DELIVERY_POLL_MS = 15_000;

/**
 * Is any message's email about to change on its own (queued and due, or claimed by the drain)?
 * A WAITING one is not: it waits for a confirmation, tomorrow's limit or staff, and asking every
 * 15 seconds would change nothing.
 */
export function deliveryPending(
  detail:
    | {
        messages: readonly {
          delivery: {
            status: TicketEmailStatus;
            waitingFor?: TicketEmailWait | null;
          } | null;
        }[];
      }
    | undefined,
): boolean {
  return (
    detail?.messages.some(
      (m) =>
        (m.delivery?.status === 'queued' && !m.delivery.waitingFor) ||
        m.delivery?.status === 'sending',
    ) ?? false
  );
}

/**
 * A message's badge: the customer's own words are FROM them; a staff message is for the
 * customer or internal.
 */
export function messageBadge(m: { visibility: string; authorKind: string }): string {
  if (m.authorKind === 'customer') return 'From the customer';
  return m.visibility === 'customer' ? 'For the customer' : 'Internal';
}

/** Settings' "Customer replies go to" field: the composer's link lands on it (`#…`). */
export const REPLY_ADDRESS_ANCHOR = 'customer-replies';

/** The customer link a customer's ticket came through, as the panel says it. */
const VIA_WORDS: Record<TicketViaLink, string> = {
  report: 'the job report link',
  invoice: 'the invoice link',
};

/**
 * The panel's "Raised" line, less its day: "Raised by the customer, from the job report
 * link", "Flagged by Tess", "By Olu". A customer's ticket has no creator, and is NEVER "By
 * Servwell" (the customer raised it; Servwell only carried the form).
 */
export function raisedBy(t: {
  source: TicketSource;
  viaLink: TicketViaLink | null;
  createdBy: { name: string | null } | null;
}): string {
  if (t.source === 'customer') {
    return t.viaLink && Object.hasOwn(VIA_WORDS, t.viaLink)
      ? `Raised by the customer, from ${VIA_WORDS[t.viaLink]}`
      : 'Raised by the customer';
  }
  const who = t.createdBy ? (t.createdBy.name ?? 'Former staff') : 'Servwell';
  return t.source === 'tech' ? `Flagged by ${who}` : `By ${who}`;
}

/** "Due today, 5:00 PM" or "Due Oct 7, 5:00 PM", on the shop's clock; null with none set. */
export function dueLabel(
  replyDueAt: Date | string | null,
  tz: string,
  now: number,
): string | null {
  if (replyDueAt === null) return null;
  const day = localDateOf(new Date(replyDueAt), tz);
  const time = timeInTz(replyDueAt, tz);
  return day === localDateOf(new Date(now), tz)
    ? `Due today, ${time}`
    : `Due ${dateOnly(day, { month: 'short', day: 'numeric' })}, ${time}`;
}

/** A reply-due as a `datetime-local` value on the shop's clock ("2026-10-06T17:00"). */
export function inputOfInstant(at: Date | string, tz: string): string {
  return localIso(new Date(at), tz)!.slice(0, 16);
}

/** A `datetime-local` value, read on the shop's clock (not the browser's), as an instant. */
export function instantOfInput(value: string, tz: string): Date | null {
  const m = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})$/.exec(value);
  if (!m) return null;
  return localWallTimeUtc(m[1]!, Number(m[2]) * 60 + Number(m[3]), tz);
}

/**
 * The shop's close of business: what "today, end of day" means for an Urgent ticket's reply-due
 * (Q3). 5 PM on the shop's clock; after 5 PM, the last minute of the day, so the pre-fill is
 * never already overdue.
 */
export const END_OF_DAY_MINUTES = 17 * 60;

/**
 * "Today, end of day" (Q3) as a `datetime-local` value on the shop's clock: 5 PM; after it, the
 * day's last minute; and in that last minute itself, tomorrow's 5 PM, so it is never a time
 * that has already passed.
 */
export function endOfDayInput(now: number, tz: string): string {
  const today = localDateOf(new Date(now), tz);
  const candidates = [
    localWallTimeUtc(today, END_OF_DAY_MINUTES, tz),
    localWallTimeUtc(today, 23 * 60 + 59, tz),
  ];
  const at =
    candidates.find((c) => c.getTime() > now) ??
    localWallTimeUtc(addDaysYmd(today, 1), END_OF_DAY_MINUTES, tz);
  return inputOfInstant(at, tz);
}

/** A status move the panel offers, by the write that makes it (`ticket.transition`). */
export interface TicketMove {
  to: 'open' | 'waiting_customer' | 'waiting_parts';
  label: string;
}

/**
 * The moves `ticket.transition` may make from `status`, read from the machine itself, so the
 * panel cannot offer one the server would refuse. Leaving `new` is assigning it, and resolving
 * is its own form; neither is here.
 */
export function transitionMoves(status: TicketState): TicketMove[] {
  return ticketMachine.transitions
    .filter((t) => t.from === status && t.via === 'ticket.transition')
    .map((t) => ({
      to: t.to as TicketMove['to'],
      label:
        t.to === 'open'
          ? status === 'resolved'
            ? 'Reopen'
            : 'Back to open'
          : `Waiting on ${t.to === 'waiting_customer' ? 'customer' : 'parts'}`,
    }));
}

/** Resolve is offered from every status but resolved (the machine's `ticket.resolve`). */
export function mayResolveFrom(status: TicketState): boolean {
  return ticketMachine.transitions.some(
    (t) => t.from === status && t.to === 'resolved' && t.via === 'ticket.resolve',
  );
}

/** What the Resolve form still needs, in the order it asks. Empty means it can be sent. */
export function resolveMissing(draft: {
  resolution: string;
  rootCause: string;
}): string[] {
  const missing: string[] = [];
  if (!draft.resolution.trim()) missing.push('what fixed it');
  // Own keys only: `'toString' in labels` is true, and is not a root cause.
  if (!Object.hasOwn(TICKET_ROOT_CAUSE_LABEL, draft.rootCause))
    missing.push('a root cause');
  return missing;
}

/** What the New ticket sheet still needs. Empty means it can be sent. */
export function newTicketMissing(draft: {
  customerId: string;
  title: string;
  category: string;
}): string[] {
  const missing: string[] = [];
  if (!draft.customerId) missing.push('a customer');
  if (!draft.title.trim()) missing.push('a title');
  if (!Object.hasOwn(TICKET_CATEGORY_LABEL, draft.category)) missing.push('a category');
  return missing;
}

/** The status badge's tone: what needs someone, what waits, what is done. */
export const TICKET_STATE_TONE: Record<TicketState, Tone> = {
  new: 'brand',
  open: 'info',
  waiting_customer: 'warn',
  waiting_parts: 'warn',
  resolved: 'success',
};

export const PRIORITY_TONE: Record<TicketPriority, Tone> = {
  low: 'neutral',
  normal: 'neutral',
  high: 'warn',
  urgent: 'danger',
};

/** Who may own a ticket: whoever may resolve one (the owner and the office, Q4). */
export function mayOwnTickets(role: string): boolean {
  return can(role as Role, 'ticket', 'resolve');
}

/** A label from one of the fixed lists, or the raw value for one this build does not know. */
export const labelOf = <K extends string>(
  labels: Record<K, string>,
  v: string,
): string => (Object.hasOwn(labels, v) ? labels[v as K] : v);

export const stateLabel = (s: string) => labelOf(TICKET_STATE_LABEL, s);
export const priorityLabel = (p: string) => labelOf(TICKET_PRIORITY_LABEL, p);
export const categoryLabel = (c: string) => labelOf(TICKET_CATEGORY_LABEL, c);
export const sourceLabel = (s: string) => labelOf(TICKET_SOURCE_LABEL, s);
export const rootCauseLabel = (r: string) =>
  labelOf(TICKET_ROOT_CAUSE_LABEL as Record<TicketRootCause, string>, r);

/** One entry of a ticket's history, as `ticket.get` answers it. */
export interface HistoryItem {
  name: string;
  actor: { type: string; name: string | null };
  payload: Record<string, unknown> | null;
}

const FACT_WORDS: Record<string, string> = {
  title: 'the title',
  category: 'the category',
  priority: 'the priority',
  replyDueAt: 'the reply-due time',
};

/**
 * One line of the history, in words: "Maya assigned it to Olu". Built only from the payload
 * keys the server lets through (`TICKET_EVENT_PAYLOAD`); a person named in a payload by id is
 * looked up in `nameOf` (the shop's roster), and someone not on it reads "someone".
 *
 * "The customer" is decided by WHAT happened, not by the actor alone: on a customer's ticket,
 * the form's own writes (`raiseCustomerTicket`'s `ticket.created` with `source: 'customer'`,
 * and `saveCustomerPhotos`' `attachment.added`, both as the system) are the customer's. Any
 * other system write (a sweep, an automation) keeps the system's name, so it is never
 * credited to the customer.
 */
/** Is this system event one the customer's form wrote, on the customer's ticket? */
function formWrote(e: HistoryItem, source: TicketSource): boolean {
  if (source !== 'customer') return false;
  if (e.name === 'ticket.created') return e.payload?.source === 'customer';
  return e.name === 'attachment.added';
}

export function historyLine(
  e: HistoryItem,
  nameOf: (id: string) => string | null,
  source: TicketSource,
): string {
  const who =
    e.actor.name ??
    (e.actor.type === 'user'
      ? 'Former staff'
      : e.actor.type === 'system'
        ? formWrote(e, source)
          ? 'The customer'
          : 'Servwell'
        : 'Someone');
  const p = e.payload ?? {};
  const person = (id: unknown) =>
    typeof id === 'string' ? (nameOf(id) ?? 'someone no longer here') : 'someone';
  switch (e.name) {
    case 'ticket.created':
      return typeof p.via === 'string' && Object.hasOwn(VIA_WORDS, p.via)
        ? `${who} raised it, from ${VIA_WORDS[p.via as TicketViaLink]}`
        : `${who} raised it`;
    case 'ticket.updated': {
      const changed = Object.keys(FACT_WORDS).filter((k) => Object.hasOwn(p, k));
      return changed.length
        ? `${who} changed ${changed.map((k) => FACT_WORDS[k]).join(', ')}`
        : `${who} changed it`;
    }
    case 'ticket.assigned':
      return `${who} assigned it to ${person(p.ownerId)}`;
    case 'ticket.transitioned':
      return `${who} moved it from ${stateLabel(String(p.from))} to ${stateLabel(String(p.to))}`;
    case 'ticket.resolved':
      return typeof p.rootCause === 'string'
        ? `${who} resolved it (root cause: ${rootCauseLabel(p.rootCause)})`
        : `${who} resolved it`;
    case 'ticket.messaged':
      return p.visibility === 'customer'
        ? `${who} wrote a message for the customer`
        : `${who} wrote an internal message`;
    case 'ticket.followed':
      return `${who} followed it`;
    case 'ticket.unfollowed':
      return `${who} stopped following it`;
    case 'attachment.added':
      return `${who} added a photo`;
    case 'attachment.removed':
      return `${who} removed a photo`;
    default:
      return `${who}: ${e.name}`;
  }
}
